<template>
  <ContainerInfoItem
    source="call_disposition_analytics"
    title="Call disposition analytics"
    description="Last 30 days"
    height="fit-content"
  >
    <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

    <BarChart
      v-else-if="chartCategories && chartData"
      id="call_disposition_analytics"
      :categories="chartCategories"
      :series-data="chartData"
    />

    <EmptyScreen
      v-else
      source="sales_activity_by_user_graph"
      text="No data found!"
      :type="emptyScreenTypes.CHART_AND_TABLE"
    />
  </ContainerInfoItem>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import BarChart from "@/src/components/charts/BarChart.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);
const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const toFetchCallDispositionAnalytics = async () => {
  try {
    isLoading.value = true;

    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.fetchCallDispositionAnalytics(params);
    if (response?.success && response?.data?.data) {
      const data = response?.data?.data;
      chartCategories.value = data.map((item) => {
        const disposition = item.disposition || "Unknown";
        return disposition.length > 20 ? disposition.substring(0, 20) + "..." : disposition;
      });
      chartData.value = [
        {
          name: "Calls",
          data: data.map((item) => item.count),
        },
      ];
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error("An error occurred while fetching call disposition analytics: ", error);
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) toFetchCallDispositionAnalytics();
});

useAnalyticsRefresh(toFetchCallDispositionAnalytics, [range, userIds, teamIds]);
</script>
